import React from 'react';
import {
  Box,
  Card,
  Typography,
  Chip,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import { Calendar, CheckCircle, XCircle, Clock, Trash2, Undo2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { leaveService } from '@/services/api';
import { LeaveRequest, LeaveStatus } from '@/types';
import { useAppSelector } from '../../../store';
import { selectCurrentUser } from '../../../store/slices/authSlice';
import dayjs from 'dayjs';
import { QUERY_KEYS } from '../../../services/queryClient';

const LeaveRequestsWidget: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const queryClient = useQueryClient();

  const { data: leaveResponse, isLoading } = useQuery({
    queryKey: [...QUERY_KEYS.myLeaves, currentUser?.id ?? ''],
    queryFn: () => leaveService.getMyLeaveRequests(),
    enabled: !!currentUser,
  });
  const leaveRequests: LeaveRequest[] = leaveResponse?.data ?? [];

  const cancelLeaveMutation = useMutation({
    mutationFn: (leaveId: string) => leaveService.cancelLeaveRequest(leaveId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.myLeaves });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.calendarEvents });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.leaveBalance });
    },
  });

  const withdrawLeaveMutation = useMutation({
    mutationFn: (leaveId: string) => leaveService.withdrawLeaveRequest(leaveId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.myLeaves });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.calendarEvents });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.leaveBalance });
    },
  });

  const getStatusChip = (status: LeaveStatus) => {
    const configs = {
      [LeaveStatus.PENDING]: {
        label: 'Pending',
        color: 'warning' as const,
        icon: <Clock size={14} />,
      },
      [LeaveStatus.APPROVED]: {
        label: 'Approved',
        color: 'success' as const,
        icon: <CheckCircle size={14} />,
      },
      [LeaveStatus.REJECTED]: {
        label: 'Rejected',
        color: 'error' as const,
        icon: <XCircle size={14} />,
      },
      [LeaveStatus.CANCELLED]: {
        label: 'Cancelled',
        color: 'default' as const,
        icon: <XCircle size={14} />,
      },
      [LeaveStatus.WITHDRAWN]: {
        label: 'Withdrawn',
        color: 'default' as const,
        icon: <Undo2 size={14} />,
      },
    };

    const config = configs[status];
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        icon={config.icon}
        sx={{ fontWeight: 500 }}
      />
    );
  };

  const sortedLeaves = [...leaveRequests].sort((a, b) => {
    // Pending first, then sort by start date descending
    if (a.status === LeaveStatus.PENDING && b.status !== LeaveStatus.PENDING) return -1;
    if (a.status !== LeaveStatus.PENDING && b.status === LeaveStatus.PENDING) return 1;
    return dayjs(b.startDate).diff(dayjs(a.startDate));
  });

  const handleCancelLeave = (leaveId: string) => {
    if (window.confirm('Are you sure you want to cancel this leave request?')) {
      cancelLeaveMutation.mutate(leaveId);
    }
  };

  const handleWithdrawLeave = (leaveId: string) => {
    if (window.confirm('Are you sure you want to withdraw this leave request? This action cannot be undone.')) {
      withdrawLeaveMutation.mutate(leaveId);
    }
  };

  return (
    <Card
      sx={{
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Calendar size={20} color="#4F46E5" />
        <Typography variant="h6" fontWeight={600}>
          My Leave Requests
        </Typography>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {isLoading ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Loading...</Typography>
        </Box>
      ) : leaveRequests.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No leave requests yet</Typography>
        </Box>
      ) : (
        <Stack spacing={2} sx={{ overflowY: 'auto', maxHeight: 400 }}>
          {sortedLeaves.map((leave) => (
            <Card
              key={leave.id}
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 1,
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                  borderColor: 'primary.main',
                },
                transition: 'all 0.2s',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                    {leave.type.replace('_', ' ')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {dayjs(leave.startDate).format('MMM DD')} -{' '}
                    {dayjs(leave.endDate).format('MMM DD, YYYY')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {leave.totalDays} {leave.totalDays === 1 ? 'day' : 'days'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getStatusChip(leave.status)}
                  {leave.status === LeaveStatus.PENDING && (
                    <Tooltip title="Cancel Leave">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleCancelLeave(leave.id)}
                        disabled={cancelLeaveMutation.isPending}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {(leave.status === LeaveStatus.APPROVED || leave.status === LeaveStatus.PENDING) && (
                    <Tooltip title="Withdraw Leave">
                      <IconButton
                        size="small"
                        color="warning"
                        onClick={() => handleWithdrawLeave(leave.id)}
                        disabled={withdrawLeaveMutation.isPending}
                      >
                        <Undo2 size={16} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>

              {leave.reason && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                  "{leave.reason}"
                </Typography>
              )}

              {leave.status === LeaveStatus.APPROVED && leave.approverComments && (
                <Box sx={{ mt: 1, p: 1, bgcolor: 'success.50', borderRadius: 1 }}>
                  <Typography variant="caption" color="success.dark">
                    Approved: {leave.approverComments}
                  </Typography>
                </Box>
              )}

              {leave.status === LeaveStatus.REJECTED && leave.approverComments && (
                <Box sx={{ mt: 1, p: 1, bgcolor: 'error.50', borderRadius: 1 }}>
                  <Typography variant="caption" color="error.dark">
                    Rejected: {leave.approverComments}
                  </Typography>
                </Box>
              )}
            </Card>
          ))}
        </Stack>
      )}
    </Card>
  );
};

export default LeaveRequestsWidget;
